<template>
  <!-- 交易明細右側抽屜面板 -->
  <Teleport to="body">
    <div v-if="modelValue" class="transaction-drawer-mask" @click.self="close">
        <div class="transaction-drawer">
            <div class="transaction-drawer-topbar">
                <div class="transaction-drawer-nav">
                    <button type="button" class="drawer-nav-btn" aria-label="Previous transaction" :disabled="!hasPrev" @click="$emit('prev')">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15 6l-6 6 6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                    <button type="button" class="drawer-nav-btn" aria-label="Next transaction" :disabled="!hasNext" @click="$emit('next')">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </div>
                <button type="button" class="drawer-close-btn" @click="close" aria-label="Close">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>
            </div>

            <div class="transaction-drawer-body">
                <div class="drawer-merchant">{{ detail.merchant }}</div>
                <div class="drawer-amount">{{ detail.amount }}</div>

                <hr class="drawer-divider">

                <div class="drawer-row">
                    <span class="drawer-label">Date</span>
                    <span class="drawer-value">{{ detail.date }}</span>
                </div>
                <div class="drawer-row">
                    <span class="drawer-label">Time</span>
                    <span class="drawer-value">{{ detail.time }}</span>
                </div>

                <hr class="drawer-divider">

                <template v-if="detail.cardType">
                    <div class="drawer-row">
                        <span class="drawer-label">Card type</span>
                        <span class="drawer-value drawer-card-type">{{ detail.cardType }}</span>
                    </div>

                    <hr class="drawer-divider">
                </template>

                <div class="drawer-row">
                    <span class="drawer-label">Payment status</span>
                    <span class="drawer-value drawer-status">{{ detail.paymentStatus }}</span>
                </div>
                <div class="drawer-row">
                    <span class="drawer-label">Reference</span>
                    <span class="drawer-value drawer-reference">{{ detail.reference }}</span>
                </div>
            </div>
        </div>
    </div>
  </Teleport>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue'

defineProps({
    // 是否顯示（v-model）
    modelValue: {
        type: Boolean,
        default: false
    },
    // 交易明細資料
    detail: {
        type: Object,
        default: () => ({
            merchant: '',
            amount: '',
            date: '',
            time: '',
            cardType: '',
            paymentStatus: '',
            reference: ''
        })
    },
    hasPrev: {
        type: Boolean,
        default: false
    },
    hasNext: {
        type: Boolean,
        default: false
    }
})

const emit = defineEmits(['update:modelValue', 'close', 'prev', 'next'])

function close() {
    emit('update:modelValue', false)
    emit('close')
}
</script>

<style scoped>
.transaction-drawer-mask {
    position: fixed;
    inset: 0;
    background: rgba(5, 28, 44, 0.45);
    z-index: 5000;
    display: flex;
    justify-content: flex-end;
}

.transaction-drawer {
    width: 100%;
    max-width: 340px;
    height: 100%;
    background: #fff;
    box-shadow: -8px 0 32px rgba(0, 0, 0, 0.18);
    padding: 20px 24px 32px 24px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
}

/* 手機版：滿版顯示，且不遮住上方 taxi1 header */
@media (max-width: 768px) {
    .transaction-drawer-mask {
        top: 80px;
    }

    .transaction-drawer {
        max-width: 100%;
    }
}

.transaction-drawer-topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 28px;
}

.transaction-drawer-nav {
    display: flex;
    align-items: center;
    gap: 8px;
}

.drawer-nav-btn,
.drawer-close-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    color: #5b4cff;
}

.drawer-nav-btn:disabled {
    color: #C5CDD4;
    cursor: not-allowed;
}

.transaction-drawer-body {
    display: flex;
    flex-direction: column;
}

.drawer-merchant {
    font-family: 'Mulish';
    font-size: 15px;
    font-weight: 500;
    color: #051C2C;
    margin-bottom: 8px;
}

.drawer-amount {
    font-family: 'Euclid Square';
    font-size: 34px;
    font-weight: 600;
    color: #051C2C;
    line-height: 1.1;
}

.drawer-divider {
    border: none;
    border-top: 1px solid #E5E5E5;
    margin: 22px 0;
}

.drawer-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
    margin-bottom: 14px;
}

.drawer-row:last-child {
    margin-bottom: 0;
}

.drawer-label {
    font-family: 'Mulish';
    font-size: 14px;
    color: #5E707D;
    flex-shrink: 0;
}

.drawer-value {
    font-family: 'Mulish';
    font-size: 14px;
    font-weight: 500;
    color: #051C2C;
    text-align: right;
    word-break: break-word;
}

.drawer-card-type {
    font-weight: 700;
    font-style: italic;
    color: #1a1f71;
}

.drawer-status {
    color: #00C962;
    font-weight: 600;
}

.drawer-reference {
    max-width: 200px;
}
</style>
